import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Building2, User } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SERVICE_GROUPS } from "@/data/services";
import { useEnquiry } from "@/context/EnquiryContext";
import "./HomeInteractive.css";

const TOTAL = SERVICE_GROUPS.reduce((n, g) => n + g.items.length, 0);

const useCountUp = (target) => {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / 900, 1);
        setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.3 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [target]);
  return [ref, val];
};

const PathwayCard = ({ g, delay }) => {
  const { openEnquiry } = useEnquiry();
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    ref.current.style.setProperty("--mx", `${x}px`); ref.current.style.setProperty("--my", `${y}px`);
    ref.current.style.setProperty("--rx", `${(0.5 - y / r.height) * 2.6}deg`); ref.current.style.setProperty("--ry", `${(x / r.width - 0.5) * 2.6}deg`);
  };
  const onLeave = () => { ref.current.style.setProperty("--rx", "0deg"); ref.current.style.setProperty("--ry", "0deg"); };
  return (
    <Reveal delay={delay} as="article" ref={ref} onPointerMove={onMove} onPointerLeave={onLeave} className="as-card" data-testid={`services-teaser-${g.key}`}>
      <span className="as-card-glow" aria-hidden />
      <header className="as-card-head">
        <span className="as-icon">{g.key === "individuals" ? <User size={16} /> : <Building2 size={16} />}</span>
        <div><span className="as-kicker">{g.key === "individuals" ? "Personal pathway" : "Business pathway"}</span><h3>{g.label}</h3></div>
        <span className="as-count">{String(g.items.length).padStart(2, "0")}</span>
      </header>
      <div className="as-list">
        {g.items.map((s) => {
          const isOpen = open === s.slug;
          return (
            <div key={s.slug} className={`as-service${isOpen ? " is-open" : ""}`}>
              <button type="button" data-testid={`services-teaser-link-${s.slug}`} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : s.slug)} className="as-service-btn">
                <span className="as-idx">{s.n}</span><span className="as-name">{s.title}</span><span className="as-plus" aria-hidden />
              </button>
              <div className="as-detail" data-testid={`services-teaser-detail-${s.slug}`}>
                <div>
                  <p>{s.body}</p>
                  <div className="as-detail-links">
                    <Link to={`/services#svc-${s.slug}`}>Learn more <ArrowUpRight size={12} /></Link>
                    <button type="button" data-testid={`services-teaser-enquire-${s.slug}`} onClick={() => openEnquiry("service", { topic: s.title })}>Discuss {s.title.toLowerCase()} <ArrowUpRight size={12} /></button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Reveal>
  );
};

export const ServicesTeaser = () => {
  const [countRef, count] = useCountUp(TOTAL);
  return (
    <section id="services" data-testid="services-teaser" className="section theme-sand as-section relative overflow-hidden">
      <div className="as-ambient" aria-hidden><span className="as-orbit as-orbit-one" /><span className="as-orbit as-orbit-two" /><span className="as-glow" /></div>
      <div className="af-wrap relative z-[2] grid lg:grid-cols-[.9fr_1.1fr] gap-12 lg:gap-20">
        <Reveal className="lg:sticky lg:top-32 self-start">
          <p className="eyebrow">How we can help</p>
          <h2 className="display">Advice for every stage of <em>life and business.</em></h2>
          <p className="lede mt-6">Thirteen services, one relationship. Every Affinity advisor is backed by specialist pathways across the group, giving you depth well beyond a single product.</p>
          <div className="as-proof" ref={countRef} data-testid="services-teaser-count">
            <span className="as-proof-num">{String(count).padStart(2, "0")}</span>
            <span className="as-proof-copy">services across<br /><strong>2 specialist pathways</strong></span>
          </div>
          <Link data-testid="services-teaser-all" to="/services" className="btn btn-outline-dark mt-8">Explore all services <ArrowRight size={16} /></Link>
        </Reveal>
        <div>
          <p className="as-guide" aria-hidden><span className="as-guide-pulse" /> Choose a pathway, then select a service</p>
          <div className="as-card-grid">
            <PathwayCard g={SERVICE_GROUPS[0]} delay={1} />
            <PathwayCard g={SERVICE_GROUPS[1]} delay={2} />
          </div>
        </div>
      </div>
    </section>
  );
};
